import { useState, useEffect } from 'react';
import { Bot, Check, Loader2, AlertCircle, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  type AIProvider,
  getAISettings,
  saveAISettings,
  testAIConnection,
  getAvailableProviders,
  isAIConfigured,
} from '../lib/ai';

interface AISettingsDialogProps {
  trigger?: React.ReactNode;
  onSettingsChange?: () => void;
}

export function AISettingsDialog({ trigger, onSettingsChange }: AISettingsDialogProps) {
  const [open, setOpen] = useState(false);
  const [provider, setProvider] = useState<AIProvider | ''>('');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const providers = getAvailableProviders();

  useEffect(() => {
    if (open) {
      const settings = getAISettings();
      setProvider(settings.provider || '');
      setApiKey(settings.apiKey || '');
      setTestResult(null);
    }
  }, [open]);

  const handleSave = () => {
    if (!provider) {
      toast.error('AI 제공자를 선택해주세요');
      return;
    }

    if (!apiKey.trim()) {
      toast.error('API 키를 입력해주세요');
      return;
    }

    saveAISettings({
      provider: provider as AIProvider,
      apiKey: apiKey.trim(),
    });

    toast.success('AI 설정이 저장되었습니다');
    onSettingsChange?.();
    setOpen(false);
  };

  const handleTest = async () => {
    if (!provider || !apiKey.trim()) {
      toast.error('먼저 제공자와 API 키를 입력해주세요');
      return;
    }

    // Temporarily save settings for testing
    saveAISettings({
      provider: provider as AIProvider,
      apiKey: apiKey.trim(),
    });

    setTesting(true);
    setTestResult(null);

    try {
      const result = await testAIConnection();
      setTestResult(result);
      if (result.success) {
        toast.success('연결 테스트 성공!');
      } else {
        toast.error(result.message);
      }
    } catch (error: any) {
      setTestResult({ success: false, message: error.message });
      toast.error('연결 테스트 실패');
    } finally {
      setTesting(false);
    }
  };

  const handleClear = () => {
    saveAISettings({ provider: null, apiKey: null });
    setProvider('');
    setApiKey('');
    setTestResult(null);
    toast.success('AI 설정이 초기화되었습니다');
    onSettingsChange?.();
  };

  const getApiKeyPlaceholder = () => {
    switch (provider) {
      case 'openai':
        return 'sk-...';
      case 'claude':
        return 'sk-ant-...';
      case 'groq':
        return 'gsk_...';
      default:
        return 'API 키를 입력하세요';
    }
  };

  const getApiKeyHelp = () => {
    switch (provider) {
      case 'openai':
        return 'https://platform.openai.com/api-keys 에서 발급받을 수 있습니다.';
      case 'claude':
        return 'https://console.anthropic.com/settings/keys 에서 발급받을 수 있습니다.';
      case 'groq':
        return 'https://console.groq.com/keys 에서 무료로 발급받을 수 있습니다.';
      default:
        return '';
    }
  };

  const isConfigured = isAIConfigured();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Bot className="w-4 h-4 mr-1.5" />
            AI 설정
            {isConfigured && <Check className="w-3 h-3 ml-1 text-green-500" />}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            AI 설정
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="ai-provider">AI 제공자</Label>
            <Select value={provider} onValueChange={(v) => setProvider(v as AIProvider)}>
              <SelectTrigger id="ai-provider">
                <SelectValue placeholder="AI 제공자 선택" />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                    {p.id === 'groq' && (
                      <span className="ml-2 text-xs text-green-600">(무료 티어)</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="api-key">API 키</Label>
            <div className="relative">
              <Input
                id="api-key"
                type={showKey ? 'text' : 'password'}
                placeholder={getApiKeyPlaceholder()}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setShowKey(!showKey)}
              >
                {showKey ? (
                  <EyeOff className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <Eye className="w-4 h-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            {provider && (
              <p className="text-xs text-muted-foreground">{getApiKeyHelp()}</p>
            )}
          </div>

          {testResult && (
            <div
              className={`flex items-center gap-2 p-3 rounded-md text-sm ${
                testResult.success
                  ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300'
                  : 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
              }`}
            >
              {testResult.success ? (
                <Check className="w-4 h-4 shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 shrink-0" />
              )}
              {testResult.message}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleTest}
              disabled={testing || !provider || !apiKey.trim()}
              className="flex-1"
            >
              {testing ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Bot className="w-4 h-4 mr-1.5" />
              )}
              연결 테스트
            </Button>
            <Button onClick={handleSave} className="flex-1">
              저장
            </Button>
          </div>

          {isConfigured && (
            <Button
              type="button"
              variant="ghost"
              onClick={handleClear}
              className="w-full text-muted-foreground hover:text-destructive"
            >
              설정 초기화
            </Button>
          )}

          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium mb-2">AI 기능</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• 자동 태그 제안: URL, 제목, 설명을 분석하여 관련 태그 추천</li>
              <li>• AI 요약: 북마크 내용을 1-2문장으로 요약</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
